import { createEffect, createSignal, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { Trash2 } from "@fleet/icons";
import { createPowerSyncQuery } from "@custodia/shared/solid";
import {
  assignProfileToAulaMutation,
  type AulaRecord,
  createSupervisedProfileMutation,
  defaultDisclosure,
  deleteSupervisedProfileMutation,
  type Disclosure,
  emptyPolicy,
  getAulasQuery,
  parseJsonColumn,
  queryKeys,
  type SupervisedProfileRecord,
  updateSupervisedProfileMutation,
} from "@custodia/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile?: SupervisedProfileRecord | null;
}

export function ProfileDialog(props: ProfileDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const [name, setName] = createSignal("");
  const [deviceLabel, setDeviceLabel] = createSignal("");
  const [guardianLabel, setGuardianLabel] = createSignal("");
  const [aulaId, setAulaId] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  // Classrooms (school multi-org): they only arrive if the user is a member of an org.
  const aulas = createPowerSyncQuery<AulaRecord>({
    queryKey: () => queryKeys.supervision.aulas(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAulasQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  createEffect(() => {
    if (props.open) {
      setName(props.profile?.name ?? "");
      setDeviceLabel(props.profile?.device_label ?? "");
      const disclosure = parseJsonColumn<Partial<Disclosure>>(props.profile?.disclosure, {});
      setGuardianLabel(disclosure.guardian_label ?? "");
      setAulaId(props.profile?.aula_id ?? "");
    }
  });

  const isEditing = () => Boolean(props.profile);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!name().trim() || !dbInstance || !uid) {
      return;
    }
    setIsSubmitting(true);
    try {
      const guardian = guardianLabel().trim() || t(msg`Family`);
      const disclosure = defaultDisclosure(guardian, name().trim());
      const targetAula = aulas.data?.find((a) => a.id === aulaId()) ?? null;
      if (props.profile) {
        // Disclosure preserves the existing scope; only labels are updated.
        const existing = parseJsonColumn<Disclosure>(props.profile.disclosure, disclosure);
        await updateSupervisedProfileMutation(
          dbInstance,
          props.profile.id,
          {
            name: name().trim(),
            deviceLabel: deviceLabel().trim() || null,
            disclosure: {
              ...existing,
              guardian_label: guardian,
              supervised_label: name().trim(),
            },
          },
          props.profile.organization_id,
        );
        // Classroom assignment (school multi-org): shares the profile with the classroom's
        // organization; the write path re-validates (same org, live classroom).
        if ((props.profile.aula_id ?? "") !== aulaId()) {
          await assignProfileToAulaMutation(
            dbInstance,
            props.profile.id,
            targetAula,
            props.profile.organization_id,
          );
        }
      } else {
        await createSupervisedProfileMutation(dbInstance, {
          userId: uid,
          organizationId: targetAula?.organization_id ?? null,
          aulaId: targetAula?.id ?? null,
          name: name().trim(),
          deviceLabel: deviceLabel().trim() || null,
          disclosure,
          policy: emptyPolicy(),
        });
      }
      props.onOpenChange(false);
      showToast(props.profile ? t(msg`Profile saved`) : t(msg`Profile created`), "success");
    } catch (err) {
      console.error("Error saving supervised profile:", err);
      showToast(t(msg`Could not save the profile. Try again.`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const dbInstance = drizzle();
    if (!dbInstance || !props.profile) {
      return;
    }
    try {
      await deleteSupervisedProfileMutation(dbInstance, props.profile.id);
      props.onOpenChange(false);
      showToast(t(msg`Profile removed`), "success");
    } catch (err) {
      console.error("Error deleting supervised profile:", err);
      showToast(t(msg`Could not remove the profile. Try again.`), "error");
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">
          {isEditing() ? t(msg`Edit profile`) : t(msg`New supervised profile`)}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={(e) => void handleSubmit(e)} class="space-y-4 p-6">
        <div class="space-y-2">
          <Label for="profile-name">{t(msg`Who is supervised`)}</Label>
          <TextField
            id="profile-name"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder={t(msg`e.g. Ana, 12`)}
            autofocus
          />
        </div>

        <div class="space-y-2">
          <Label for="profile-device">{t(msg`Device`)}</Label>
          <TextField
            id="profile-device"
            value={deviceLabel()}
            onInput={(e) => setDeviceLabel(e.currentTarget.value)}
            placeholder={t(msg`e.g. Ana's tablet`)}
          />
        </div>

        <div class="space-y-2">
          <Label for="profile-guardian">{t(msg`Supervisor label`)}</Label>
          <TextField
            id="profile-guardian"
            value={guardianLabel()}
            onInput={(e) => setGuardianLabel(e.currentTarget.value)}
            placeholder={t(msg`e.g. García family`)}
          />
          <p class="text-xs text-muted-foreground">
            {t(
              msg`This name is shown to the supervised person in the transparency notice. Supervision is never hidden.`,
            )}
          </p>
        </div>

        <Show when={(aulas.data?.length ?? 0) > 0}>
          <div class="space-y-2">
            <Label for="profile-aula">{t(msg`Classroom (optional)`)}</Label>
            <Select
              id="profile-aula"
              value={aulaId()}
              onChange={setAulaId}
              options={[
                { value: "", label: t(msg`No classroom`) },
                ...(aulas.data ?? []).map((a) => ({ value: a.id, label: a.name ?? a.id })),
              ]}
              data-testid="profile-aula-select"
            />
            <p class="text-xs text-muted-foreground">
              {t(msg`Assigning a classroom shares this profile with that school's organization.`)}
            </p>
          </div>
        </Show>

        <div class="flex items-center justify-between pt-4 border-t border-border/10">
          <Show when={isEditing()}>
            <ConfirmDialog
              title={t(msg`Remove profile?`)}
              description={t(
                msg`This stops supervision for this profile. The audit trail is preserved.`,
              )}
              confirmLabel={t(msg`Remove`)}
              variant="destructive"
              onConfirm={() => void handleDelete()}
              renderTrigger={(open) => (
                <Button
                  type="button"
                  variant="ghost"
                  class="text-destructive hover:bg-destructive/10"
                  onClick={open}
                >
                  <Trash2 class="h-4 w-4 mr-2" />
                  {t(msg`Remove`)}
                </Button>
              )}
            />
          </Show>
          <div class="flex items-center gap-3 ml-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                props.onOpenChange(false);
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting() || !name().trim() || !drizzle()}
              class="disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isEditing() ? t(msg`Save changes`) : t(msg`Create profile`)}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
